/**
 * Quotation CRUD + types + computation helpers.
 *
 * Production-grade priced proposal sent from a studio to a client.
 * Path: `quotations/{quotationId}` + an append-only revisions
 * subcollection at `quotations/{id}/revisions/{revId}`.
 *
 * Each save (after the initial draft) snapshots the FULL quote into
 * the revisions subcollection AND bumps `version` on the parent.
 * The studio + client can scroll back through every prior version
 * without ever losing data.
 *
 * Data model — Indian SaaS norms:
 *   - Per-section line items grouped by room / module
 *   - Subtotal → discount (% or flat) → taxable → GST (intra-CGST+SGST
 *     or inter-IGST) → round-off → grand total
 *   - Inline payment-milestone schedule with auto-distribution
 *   - Bank details + UPI QR toggles for the printed PDF
 *   - Number-in-words (Indian lakh / crore system)
 *   - Snapshotted org letterhead (name, GSTIN, address, signature
 *     URL) so historic quotes don't change when the studio re-brands
 */
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  type FieldValue,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

// ───── Enums ─────

export type QuotationStatus =
  | 'draft'
  | 'sent'
  | 'accepted'
  | 'rejected'
  | 'expired';

export const QUOTATION_STATUSES: {
  key: QuotationStatus;
  label: string;
  bg: string;
  fg: string;
}[] = [
  { key: 'draft',    label: 'Draft',    bg: '#F1F5F9', fg: '#475569' },
  { key: 'sent',     label: 'Sent',     bg: '#DBEAFE', fg: '#1D4ED8' },
  { key: 'accepted', label: 'Accepted', bg: '#DCFCE7', fg: '#15803D' },
  { key: 'rejected', label: 'Rejected', bg: '#FEE2E2', fg: '#B91C1C' },
  { key: 'expired',  label: 'Expired',  bg: '#FEF3C7', fg: '#B45309' },
];

export function quotationStatusLabel(s: QuotationStatus): string {
  return QUOTATION_STATUSES.find((x) => x.key === s)?.label ?? s;
}
export function quotationStatusTone(s: QuotationStatus): { bg: string; fg: string } {
  const x = QUOTATION_STATUSES.find((q) => q.key === s);
  return x ? { bg: x.bg, fg: x.fg } : { bg: '#F1F5F9', fg: '#475569' };
}

/** Units used in Indian interior quotes. */
export const QUOTATION_UNITS = [
  'Sq.ft',
  'Rft',
  'Cft', // cubic feet — for items priced by volume (e.g. modular boxes, shelving)
  'Cum', // cubic metre — used by some studios that quote in metric volume
  'Nos',
  'L.S.',
  'Set',
  'Kg',
  'Ltr',
  'Hrs',
  'Day',
] as const;
export type QuotationUnit = (typeof QUOTATION_UNITS)[number];

export const GST_RATES = [0, 5, 12, 18, 28] as const;
export type GstRate = (typeof GST_RATES)[number];

export type DiscountType = 'percent' | 'flat';

/** Unit a line item's width/height are measured in. Independent
 *  from the line item's billing `unit` (which might be Sq.ft
 *  while dimensions are in metres — we convert internally). */
export type DimensionUnit = 'ft' | 'in' | 'm' | 'cm' | 'mm';

export const DIMENSION_UNITS: DimensionUnit[] = ['ft', 'in', 'm', 'cm', 'mm'];

// ───── Doc shape ─────

export type QuotationItem = {
  id: string;
  name: string;
  description?: string;
  /** Optional brand / make string. */
  brand?: string;
  /** HSN / SAC code for GST filings. */
  hsn?: string;
  unit: string;
  quantity: number;
  rate: number;
  /** Per-line discount percent (0-100). Applied AFTER quantity*rate
   *  but BEFORE the module subtotal sum. Defaults to 0 when omitted. */
  discount?: number;
  /** Cached `quantity * rate * (1 - discount/100)`. Always trust the
   *  value computed at save time so totals stay consistent without
   *  a recompute. */
  amount: number;
  // ─── Cost + markup workflow (internal margin tracking) ───
  // When the studio enters `costPrice` (their internal cost
  // per unit) + a markup, the form auto-derives `rate` (the
  // selling price the client sees). The user can still type
  // `rate` directly — in that case `markupValue` recomputes
  // as the implied delta from `costPrice`, keeping both views
  // consistent. Saved alongside `rate` so margin reports can
  // be built without recomputing from history. NEVER printed
  // on the client-facing PDF — purely internal.
  costPrice?: number;
  markupType?: DiscountType;     // 'percent' | 'flat'
  markupValue?: number;

  // ─── Supply + Installation rate split ───
  // Interior-design specific: wardrobes, false ceilings,
  // modular kitchens often bill material (supply) separately
  // from labour (installation). When both are set, the form
  // computes `rate = supplyRate + installRate` and the
  // PRINTED PDF surfaces the split inline ("Supply ₹2,000 +
  // Install ₹500 per Sq.ft") so clients understand the
  // breakdown. Leave both unset for a single bundled rate.
  supplyRate?: number;
  installRate?: number;

  // ─── Dimension-based qty (interior items like flooring,
  // walls, wardrobes, false ceiling). When count + width +
  // height (+ optional depth) are set AND the billing `unit`
  // is area-based (Sq.ft) or length-based (Rft) or volume-
  // based (Cft), the form auto-fills `quantity` via
  // `deriveQuantityFromDimensions()`. For other billing units
  // the dimensions stay informational — printed on the quote
  // so the client can verify the spec, but the user enters
  // qty manually. ───
  /** Repetition count — "3 wardrobes" or "12 panels". Defaults
   *  to 1 when undefined so existing items continue to derive
   *  qty as count=1 × width × height. */
  count?: number;
  width?: number;
  height?: number;
  /** Depth (the third dimension). Only used for Cft (cubic
   *  feet) billing. Saved for reference even when not used in
   *  the qty derivation so the printed spec line stays
   *  complete. */
  depth?: number;
  dimensionUnit?: DimensionUnit;
};

export type QuotationSection = {
  id: string;
  title: string;
  items: QuotationItem[];
};

export type GstConfig = {
  enabled: boolean;
  /** 'intra' splits into CGST+SGST (same state). 'inter' = single
   *  IGST (different state, including SEZ). */
  type: 'intra' | 'inter';
  rate: GstRate;
};

export type PaymentMilestone = {
  id: string;
  label: string;
  /** Percent of grand total. */
  percent: number;
  /** Cached rupee amount at save time. */
  amount: number;
};

export type BankDetails = {
  accountName?: string;
  bankName?: string;
  branch?: string;
  accountNumber?: string;
  ifsc?: string;
  upiId?: string;
};

export type Quotation = {
  id: string;
  orgId: string;
  /** Sequential reference like "QT-2605-001". */
  number: string;
  /** Revision counter starting at 0 for the first save. */
  version: number;
  status: QuotationStatus;
  // Dates
  date: Timestamp | null;
  validUntil: Timestamp | null;
  // Client snapshot (so historic quotes never change even if the
  // party doc is later edited).
  clientId: string | null;
  clientName: string;
  clientPhone?: string;
  clientEmail?: string;
  clientAddress?: string;
  clientGstin?: string;
  // Optional project link
  projectId?: string | null;
  projectName?: string;
  // Body
  sections: QuotationSection[];
  // Financials
  subtotal: number;
  discountType: DiscountType;
  discountValue: number;
  discountAmount: number;
  taxableAmount: number;
  gst: GstConfig;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  taxAmount: number;
  roundOff: number;
  total: number;
  // Tail
  paymentMilestones: PaymentMilestone[];
  termsAndConditions: string[];
  notes?: string;
  // Branding snapshot
  companyName: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyGstin?: string;
  companyLogoUrl?: string;
  companySignatureUrl?: string;
  bankDetails?: BankDetails;
  showBankDetails: boolean;
  showSignature: boolean;
  // Audit
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  createdBy: string;
  createdByName?: string;
  /** Set when status === 'accepted'. */
  acceptedAt?: Timestamp | null;
  acceptedBy?: string;
  /** Set when status === 'rejected'. */
  rejectedAt?: Timestamp | null;
  rejectionReason?: string;
};

/** A revision snapshot stored at `quotations/{id}/revisions/{revId}`. */
export type QuotationRevision = {
  id: string;
  quotationId: string;
  version: number;
  /** Reason the studio gave when creating this revision. */
  note: string;
  /** Frozen snapshot of the entire quote at this version. */
  snapshot: Quotation;
  createdAt: Timestamp | null;
  createdBy: string;
  createdByName?: string;
};

// ───── Defaults ─────

/** Default 5-point T&C used when the studio starts a fresh quote.
 *  Editable per-quote — these are just sensible starters. */
export const DEFAULT_TNC = [
  'This quotation is valid for 15 days from the date of issue.',
  'Payment schedule as per the milestones above. All payments via UPI / NEFT / cheque favouring the studio account.',
  'Material brand / finish substitutions, if any, will be priced separately as a variation order before execution.',
  'Project timelines assume continuous site access. Delays caused by society / RWA / building approvals are excluded.',
  'GST is charged extra at the prevailing rate. Any government levy revisions during the project will be passed through.',
];

export const DEFAULT_MILESTONES: Pick<PaymentMilestone, 'label' | 'percent'>[] = [
  { label: 'Booking advance',  percent: 10 },
  { label: 'Material order',   percent: 40 },
  { label: 'Site delivery',    percent: 40 },
  { label: 'Final handover',   percent: 10 },
];

// ───── Helpers ─────

/** Round half-up to 2 decimal places. */
function r2(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/** Multipliers to convert from each DimensionUnit to FEET — the
 *  internal reference. `Sq.ft` and `Rft` calculations all work
 *  in feet, then we round to 2 decimal places at the caller. */
const DIMENSION_TO_FEET: Record<DimensionUnit, number> = {
  ft: 1,
  in: 1 / 12,
  m: 3.28084,
  cm: 3.28084 / 100,
  mm: 3.28084 / 1000,
};

/** Compute a billing quantity from count × width × height
 *  (× depth) + dimension unit. Returns `null` when the
 *  billing `unit` doesn't have a sensible dimension derivation
 *  (Nos, Kg, Hrs — user enters qty manually).
 *
 *  Behaviour by billing unit:
 *    - 'Sq.ft' → count × width × height (both dim to feet) — area
 *    - 'Rft'   → count × width (height ignored — linear measure)
 *    - 'Cft'   → count × width × height × depth (all to feet) — volume
 *    - 'Cum'   → count × width_m × height_m × depth_m — volume in cubic metres
 *    - everything else → null
 *
 *  Result rounded to 2 decimal places so the form's qty field
 *  shows a clean number rather than 14.999999.
 *
 *  `count` defaults to 1 when omitted, preserving the
 *  pre-count behaviour for existing items. */
export function deriveQuantityFromDimensions(args: {
  count?: number;
  width?: number;
  height?: number;
  depth?: number;
  dimensionUnit?: DimensionUnit;
  unit: string;
}): number | null {
  const { count, width, height, depth, dimensionUnit, unit } = args;
  const du = dimensionUnit ?? 'ft';
  const m = DIMENSION_TO_FEET[du] ?? 1;
  // Count defaults to 1 — "single unit, just dimensioned".
  const n = count && count > 0 ? count : 1;

  if (unit === 'Sq.ft') {
    if (!width || width <= 0 || !height || height <= 0) return null;
    return r2(n * (width * m) * (height * m));
  }
  if (unit === 'Rft') {
    // Linear / running-foot — only width matters. Height +
    // depth still saved on the doc for the spec line.
    if (!width || width <= 0) return null;
    return r2(n * width * m);
  }
  if (unit === 'Cft') {
    // Cubic feet — need all three dimensions.
    if (
      !width ||
      width <= 0 ||
      !height ||
      height <= 0 ||
      !depth ||
      depth <= 0
    )
      return null;
    return r2(n * (width * m) * (height * m) * (depth * m));
  }
  if (unit === 'Cum') {
    // Cubic metres — convert each dimension to metres (1 ft =
    // 0.3048 m, etc.). Pre-multiply by 1/3.28084 to convert
    // the feet-multiplier `m` back to the metre side.
    if (
      !width ||
      width <= 0 ||
      !height ||
      height <= 0 ||
      !depth ||
      depth <= 0
    )
      return null;
    const ftToM = 1 / 3.28084;
    return r2(
      n * (width * m * ftToM) * (height * m * ftToM) * (depth * m * ftToM),
    );
  }
  // For Nos, L.S., Set, Kg, Ltr, Hrs, Day — no auto-derive.
  // Dimensions still saved + printed under the description,
  // but qty stays in the user's hands.
  return null;
}

/** Compute the per-item selling rate from cost + markup.
 *  Returns the original `rate` unchanged when no markup math
 *  is set — keeps backwards compat with quotes saved before
 *  the cost-and-markup feature shipped. */
export function deriveRateFromCostAndMarkup(args: {
  costPrice?: number;
  markupType?: DiscountType;
  markupValue?: number;
  rate?: number;
}): number {
  const { costPrice, markupType, markupValue, rate } = args;
  if (!costPrice || costPrice <= 0) return rate ?? 0;
  if (!markupValue || markupValue <= 0) return costPrice;
  if (markupType === 'flat') return r2(costPrice + markupValue);
  // Default percent.
  return r2(costPrice * (1 + markupValue / 100));
}

/** Inverse of `deriveRateFromCostAndMarkup` — given a cost +
 *  the user's manually-typed rate, derive what the markup
 *  percent would be. Used to keep the markup display in
 *  sync when the user edits `rate` directly after a
 *  cost+markup auto-fill. */
export function deriveMarkupPercentFromCostAndRate(args: {
  costPrice?: number;
  rate?: number;
}): number {
  const { costPrice, rate } = args;
  if (!costPrice || costPrice <= 0 || !rate || rate <= 0) return 0;
  return r2(((rate - costPrice) / costPrice) * 100);
}

/** Format a rupee amount using Indian numbering. Always rounds to
 *  the nearest rupee on display (GST math keeps decimals internally). */
export function formatInr(n: number): string {
  return '₹' + Math.round(n).toLocaleString('en-IN');
}

/** Compact rupee display — "₹45.6 L" / "₹2.1 Cr" / "₹12K". */
export function compactInr(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  if (abs >= 1_00_00_000) return `${sign}₹${(abs / 1_00_00_000).toFixed(2)} Cr`;
  if (abs >= 1_00_000)    return `${sign}₹${(abs / 1_00_000).toFixed(1)} L`;
  if (abs >= 1000)        return `${sign}₹${Math.round(abs / 1000)}K`;
  return `${sign}₹${Math.round(abs).toLocaleString('en-IN')}`;
}

/** Convert a rupee amount (max 99 Cr) into Indian-words form.
 *  Used in the printed quote ("In words" line on the totals row). */
export function numberToIndianWords(n: number): string {
  const num = Math.round(Math.abs(n));
  if (num === 0) return 'Zero rupees only';

  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight',
    'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen',
    'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const tens = [
    '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy',
    'Eighty', 'Ninety',
  ];

  function twoDigit(x: number): string {
    if (x < 20) return ones[x];
    const t = Math.floor(x / 10);
    const o = x % 10;
    return tens[t] + (o ? ' ' + ones[o] : '');
  }
  function threeDigit(x: number): string {
    const h = Math.floor(x / 100);
    const rest = x % 100;
    const parts: string[] = [];
    if (h) parts.push(ones[h] + ' Hundred');
    if (rest) parts.push(twoDigit(rest));
    return parts.join(' ');
  }

  // Indian numbering: crore (10^7), lakh (10^5), thousand (10^3), then 0-999.
  let remainder = num;
  const crore = Math.floor(remainder / 1_00_00_000);
  remainder = remainder % 1_00_00_000;
  const lakh = Math.floor(remainder / 1_00_000);
  remainder = remainder % 1_00_000;
  const thousand = Math.floor(remainder / 1000);
  remainder = remainder % 1000;
  const hundred = remainder;

  const parts: string[] = [];
  if (crore) parts.push(threeDigit(crore) + ' Crore');
  if (lakh) parts.push(twoDigit(lakh) + ' Lakh');
  if (thousand) parts.push(twoDigit(thousand) + ' Thousand');
  if (hundred) parts.push(threeDigit(hundred));
  const sign = n < 0 ? 'Minus ' : '';
  return sign + parts.join(' ').trim() + ' rupees only';
}

// ───── Totals computation ─────

export type ComputedTotals = {
  subtotal: number;
  discountAmount: number;
  taxableAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  taxAmount: number;
  roundOff: number;
  total: number;
};

/** Pure function — recompute the entire totals block from sections,
 *  discount, and GST config. Called both at save time (cached onto
 *  the doc) AND in the form's right-rail summary. */
export function computeQuotationTotals(args: {
  sections: QuotationSection[];
  discountType: DiscountType;
  discountValue: number;
  gst: GstConfig;
}): ComputedTotals {
  // Per-line discount is applied to (qty * rate) BEFORE the module
  // sum. Item-level discount is independent of the overall quote
  // discount in args.discountValue — both apply.
  const subtotal = args.sections.reduce(
    (s, sec) =>
      s +
      sec.items.reduce((sIt, it) => {
        const gross = it.quantity * it.rate;
        const disc = Math.max(0, Math.min(100, it.discount ?? 0));
        return sIt + r2(gross * (1 - disc / 100));
      }, 0),
    0,
  );
  const discountAmount =
    args.discountType === 'percent'
      ? r2((subtotal * Math.max(0, Math.min(100, args.discountValue))) / 100)
      : r2(Math.min(args.discountValue, subtotal));
  const taxableAmount = r2(subtotal - discountAmount);

  let cgst = 0, sgst = 0, igst = 0;
  if (args.gst.enabled && args.gst.rate > 0) {
    if (args.gst.type === 'intra') {
      const half = args.gst.rate / 2;
      cgst = r2((taxableAmount * half) / 100);
      sgst = r2((taxableAmount * half) / 100);
    } else {
      igst = r2((taxableAmount * args.gst.rate) / 100);
    }
  }
  const taxAmount = r2(cgst + sgst + igst);
  const raw = taxableAmount + taxAmount;
  const total = Math.round(raw); // nearest rupee
  const roundOff = r2(total - raw);

  return {
    subtotal: r2(subtotal),
    discountAmount,
    taxableAmount,
    cgstAmount: cgst,
    sgstAmount: sgst,
    igstAmount: igst,
    taxAmount,
    roundOff,
    total,
  };
}

/** Distribute the rounded grand total across milestones based on
 *  the percents the user set. The last milestone absorbs rounding
 *  pennies so the row totals always equal `total`. */
export function distributeMilestones(
  milestones: { id: string; label: string; percent: number }[],
  total: number,
): PaymentMilestone[] {
  if (milestones.length === 0) return [];
  let running = 0;
  const out: PaymentMilestone[] = milestones.map((m, i) => {
    const amount =
      i === milestones.length - 1
        ? Math.max(0, Math.round(total - running))
        : Math.round((total * m.percent) / 100);
    running += amount;
    return { id: m.id, label: m.label, percent: m.percent, amount };
  });
  return out;
}

// ───── Quote number generator ─────

/** Pad a number to two digits — "QT-2605-007" needs leading zeros. */
function pad(n: number, w = 2): string {
  return n.toString().padStart(w, '0');
}

/** Find the next quote number for the org. Format `QT-YYMM-NNN`
 *  where NNN is sequential per month. Uses a single read of the
 *  current month's quotes to determine the next sequence. */
export async function nextQuotationNumber(orgId: string): Promise<string> {
  const now = new Date();
  const prefix = `QT-${pad(now.getFullYear() % 100)}${pad(now.getMonth() + 1)}`;
  // Pull all quotes for the org and filter client-side (the dataset
  // is small + we don't need a composite index for this one-shot
  // lookup). Even at 10k quotes / month a single read is < 1MB.
  const snap = await getDocs(
    query(collection(db, 'quotations'), where('orgId', '==', orgId)),
  );
  let maxSeq = 0;
  snap.forEach((d) => {
    const data = d.data() as { number?: string };
    if (typeof data.number !== 'string') return;
    if (!data.number.startsWith(prefix + '-')) return;
    const tail = data.number.slice(prefix.length + 1);
    const n = parseInt(tail, 10);
    if (Number.isFinite(n) && n > maxSeq) maxSeq = n;
  });
  return `${prefix}-${pad(maxSeq + 1, 3)}`;
}

// ───── CRUD ─────

export type CreateQuotationInput = Omit<
  Quotation,
  | 'id'
  | 'createdAt'
  | 'updatedAt'
  | 'version'
  | 'acceptedAt'
  | 'rejectedAt'
>;

export async function createQuotation(
  input: CreateQuotationInput,
): Promise<string> {
  const ref = doc(collection(db, 'quotations'));
  const payload: Record<string, unknown> = {
    ...stripUndefined(input),
    version: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, payload);
  return ref.id;
}

/** Update without creating a revision — used for in-place edits
 *  while the quote is still in draft. Once the quote is sent, the
 *  caller should use `saveQuotationRevision()` instead so the
 *  history doesn't lose intermediate states. */
export async function updateQuotation(
  id: string,
  patch: Partial<Quotation>,
): Promise<void> {
  await updateDoc(doc(db, 'quotations', id), {
    ...stripUndefined(patch),
    updatedAt: serverTimestamp(),
  });
}

/** Save a new revision: writes the current quote as an immutable
 *  snapshot AND updates the live doc with the patch. Use this when
 *  the quote has already left "draft" status and a meaningful
 *  change is being made.
 *
 *  The snapshot reflects the doc BEFORE this edit, so when viewing
 *  the history list "Version 0" shows what the studio originally
 *  sent and "Version 1" shows the state after the first edit, etc.
 */
export async function saveQuotationRevision(args: {
  id: string;
  /** The quote's current state (BEFORE this edit). */
  current: Quotation;
  /** Reason for the new revision. */
  note: string;
  /** Updated fields to write to the live doc. */
  patch: Partial<Quotation>;
  createdBy: string;
  createdByName?: string;
}): Promise<void> {
  const nextVersion = (args.current.version ?? 0) + 1;
  const revRef = doc(
    collection(db, 'quotations', args.id, 'revisions'),
  );
  // Snapshot of the CURRENT state — what the quote looked like
  // before this edit. The frozen copy lives forever even if the
  // live doc is later deleted.
  const snap: Record<string, unknown> = {
    quotationId: args.id,
    version: args.current.version ?? 0,
    note: args.note.trim() || `Revision ${nextVersion}`,
    snapshot: stripUndefined(args.current as unknown as Record<string, unknown>),
    createdAt: serverTimestamp(),
    createdBy: args.createdBy,
  };
  if (args.createdByName) snap.createdByName = args.createdByName;
  await setDoc(revRef, snap);

  // Update the live doc with the patch + bump version.
  await updateDoc(doc(db, 'quotations', args.id), {
    ...stripUndefined(args.patch),
    version: nextVersion,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteQuotation(id: string): Promise<void> {
  // Revisions subcollection is left in place — Firestore allows
  // orphan subdocs and our rule blocks public read of them anyway.
  // If a future maintenance task wants to GC them, it can do a
  // collection-group sweep.
  await deleteDoc(doc(db, 'quotations', id));
}

/** Mark a quote as accepted — captures `acceptedAt` + `acceptedBy`
 *  so the audit log is clear. */
export async function acceptQuotation(args: {
  id: string;
  acceptedBy: string;
}): Promise<void> {
  await updateDoc(doc(db, 'quotations', args.id), {
    status: 'accepted',
    acceptedAt: serverTimestamp(),
    acceptedBy: args.acceptedBy,
    updatedAt: serverTimestamp(),
  });
}

/** Mark a quote as rejected with a reason for the audit log. */
export async function rejectQuotation(args: {
  id: string;
  reason: string;
}): Promise<void> {
  await updateDoc(doc(db, 'quotations', args.id), {
    status: 'rejected',
    rejectedAt: serverTimestamp(),
    rejectionReason: args.reason.trim(),
    updatedAt: serverTimestamp(),
  });
}

/** Mark a quote as sent. Idempotent — safe to call repeatedly. */
export async function markQuotationSent(id: string): Promise<void> {
  await updateDoc(doc(db, 'quotations', id), {
    status: 'sent',
    updatedAt: serverTimestamp(),
  });
}

// ───── Utility ─────

/** Firestore rejects `undefined` field values ANYWHERE in the
 *  document — top-level, nested objects, even inside array
 *  elements. A shallow strip is not enough because patches like
 *  `bankDetails: { accountName: undefined, ... }` would still
 *  reach Firestore and blow up the write.
 *
 *  This deep-walks plain objects + arrays and:
 *    - drops `undefined` values entirely (key removed)
 *    - leaves `null`, FieldValue sentinels, Timestamps, and other
 *      non-plain objects untouched (instanceof check on
 *      `Object.getPrototypeOf` differentiates them from POJOs)
 *
 *  Performance note: the entire quote doc is small (<10kb) so the
 *  recursive walk is negligible — runs in microseconds. */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object') return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

function stripUndefined<T extends Record<string, unknown>>(
  o: T,
): Record<string, unknown | FieldValue> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(o)) {
    const v = o[k];
    if (v === undefined) continue;
    if (Array.isArray(v)) {
      out[k] = v
        .filter((item) => item !== undefined)
        .map((item) => (isPlainObject(item) ? stripUndefined(item) : item));
    } else if (isPlainObject(v)) {
      out[k] = stripUndefined(v);
    } else {
      // Timestamp, FieldValue, primitives, null — passthrough.
      out[k] = v;
    }
  }
  return out as Record<string, unknown | FieldValue>;
}

/** Generate a short, URL-safe id for line items / sections /
 *  milestones. Not a doc id (those come from Firestore) — these
 *  are local-only ids for React keys + drag-reorder stability. */
export function localId(prefix = 'i'): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-4)}`;
}
